import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const RatingWrapper = styled.div`
    margin-top: 0.3em;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    font-size: 1.3em;
`;

const StarsRow = styled.div`
    display: flex;
    gap: 5px;
    justify-content: flex-end;
`;

const Star = styled.span`
    cursor: pointer;
    color: ${props => (props.active ? '#FFD700' : '#ccc')};
    transition: color 0.2s ease-in-out;
`;

const ReasonBox = styled.textarea`
    margin-top: 8px;
    width: 100%;
    max-width: 400px;
    font-size: 0.6em;
    padding: 8px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    background-color: #f9f9f9;
    color: #333;
    resize: vertical;
`;

const SubmitButton = styled.button`
    margin-top: 6px;
    padding: 5px 10px;
    background-color: var(--blue-button-color);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.6em;
    cursor: pointer;

    &:hover {
        background-color: #005fa3;
    }
`;

const RatingStars = ({ pId, initialRating = 0 }) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState(initialRating);
    const [reason, setReason] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [showReason, setShowReason] = useState(false);

    const LOG_ENDPOINT = process.env.REACT_APP_LOG_ENDPOINT;

    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    if (!uuidRegex.test(pId)) {
        console.error(t('log.invalidUuid') + ':', pId);
    }

    const handleClick = (value) => {
        setRating(value);
        if (value < 5) {
            setShowReason(true);
        } else {
            sendFeedback(value, '');
        }
    };

    const sendFeedback = async (value, reasonText) => {
        try {
            const response = await fetch(LOG_ENDPOINT, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    p_id: String(pId),
                    rating: String(value),
                    reason: reasonText || undefined,
                }),
            });
            if (!response.ok) {
                console.error(t('log.ratingFailed'));
            } else {
                setSubmitted(true);
                setShowReason(false);
            }
        } catch (error) {
            console.error(t('log.ratingError'), error);
        }
    };

    const handleReasonSubmit = () => {
        if (rating < 5 && reason.trim() === '') {
            alert(t('log.reasonRequired'));
            return;
        }
        sendFeedback(rating, reason);
    };

    if (submitted) return null;

    return (
        <RatingWrapper>
            <StarsRow>
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} active={star <= rating} onClick={() => handleClick(star)}>
                        ★
                    </Star>
                ))}
            </StarsRow>

            {showReason && (
                <>
                    <ReasonBox
                        rows={2}
                        placeholder={t('log.reasonPlaceholder')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <SubmitButton onClick={handleReasonSubmit}>
                        {t('log.submitFeedback')}
                    </SubmitButton>
                </>
            )}
        </RatingWrapper>
    );
};

export default RatingStars;
